import { useAsync, useDeepCompareEffect, useMountEffect } from "@react-hookz/web";
import merge from "deepmerge";
import { useAtomValue } from "jotai";
import defaults from "lodash/defaults";
import { useMemo, useState } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";

import { config as configData } from "shared/data";
import { useCurrentPageType, useFallbackStorageValue, useResponsive } from "shared/hooks";
import { PageType } from "shared/lib/page-type";
import { theme } from "shared/lib/styles";

import type { AppBarConfiguration } from "scmp-app/components/app-bar/types";
import type { hooksAdvertisementConfigQuery } from "scmp-app/queries/__generated__/hooksAdvertisementConfigQuery.graphql";

import { DynamicAdSlot } from "./ad-slots/ad-slot/dynamics";
import { topBannerAdSlotAtom } from "./ad-slots/top-banner-ad-slot/atoms";

export const useAdvertisementConfig = () => {
  const currentPageType = useCurrentPageType();
  const { set: setCachedConfig, value: cachedConfig } =
    useFallbackStorageValue<RawAdvertisementConfig>("advertisement-config");

  const [config, setConfig] = useState<AdvertisementConfig>(
    parseConfig(cachedConfig, currentPageType) ?? defaultAdvertisementConfig,
  );
  const environment = useRelayEnvironment();

  const [{ result: remoteConfig, status }, { execute }] = useAsync(async () => {
    // Set config in drupal
    // Ref: https://${drupal-host}/admin/config/services/app-config
    // Example: https://staging-scmp-d8.product-web.dev-2.scmp.tech/admin/config/services/app-config
    const data = await fetchQuery<hooksAdvertisementConfigQuery>(
      environment,
      graphql`
        query hooksAdvertisementConfigQuery($id: String) {
          appConfig(filter: { entityId: $id }) {
            json
          }
        }
      `,
      {
        id: configData.advertisement.configId,
      },
      {
        fetchPolicy: "store-or-network",
      },
    ).toPromise();

    if (!data?.appConfig?.json) return;
    return data.appConfig.json as RawAdvertisementConfig;
  });
  useMountEffect(execute);

  useDeepCompareEffect(() => {
    if (!remoteConfig) return;

    setConfig(parseConfig(remoteConfig, currentPageType) ?? defaultAdvertisementConfig);
    setCachedConfig(remoteConfig);
  }, [remoteConfig, setCachedConfig, currentPageType]);

  return { config, loaded: status !== "loading" && status !== "not-executed" };
};

export type AdvertisementConfig = {
  autoRefresh: {
    desktopMaximumRefreshLimit: number;
    disabled?: boolean;
    disabledCountries: string[];
    disabledResumeRefresh: boolean;
    minimalScrollAwayDurationMs: number;
    mobileMaximumRefreshLimit: number;
    refreshIntervalMs: number;
  };
  magnitePrebid: {
    skipCountries: string[];
  };
  mobileInlineAdTest: {
    fourthAdDisabledSectionIds: string[];
    fourthAdEnabledSectionIds: string[];
    isEnabled: boolean;
    isSurveyEnabled: boolean;
  };
  viewabilityTracking: {
    enable: boolean;
    eventIntervalMs: number;
    testMode: {
      enable: boolean;
      targetUrlPatterns: string[];
    };
    workerIntervalMs: number;
  };
};

export const defaultAdvertisementConfig: AdvertisementConfig = {
  autoRefresh: {
    desktopMaximumRefreshLimit: 30,
    disabledCountries: [],
    disabledResumeRefresh: false,
    minimalScrollAwayDurationMs: 5000,
    mobileMaximumRefreshLimit: 5,
    refreshIntervalMs: 20_000,
  },
  magnitePrebid: {
    skipCountries: [],
  },
  mobileInlineAdTest: {
    fourthAdDisabledSectionIds: [],
    fourthAdEnabledSectionIds: [],
    isEnabled: false,
    isSurveyEnabled: false,
  },
  viewabilityTracking: {
    enable: false,
    eventIntervalMs: 10_000,
    testMode: {
      enable: false,
      targetUrlPatterns: [],
    },
    workerIntervalMs: 15_000,
  },
};

export type RawAdvertisementConfig = {
  adViewabilityTrackingEnable?: number;
  adViewabilityTrackingEventInterval?: number;
  adViewabilityTrackingQueueWorkerInterval?: number;
  adViewabilityTrackingTargetUrls?: string[];
  adViewabilityTrackingTestMode?: number;
  autoRefreshDesktopLimit?: number;
  autoRefreshInterval?: number;
  autoRefreshMinimalScrollAwayDurationMs?: number;
  autoRefreshMobileLimit?: number;
  disableAutoRefreshCountries?: string[];
  gameAdRefreshEnable: boolean;
  gameAutoRefreshDesktopLimit: number;
  gameAutoRefreshInterval: number;
  gameAutoRefreshMobileLimit: number;
  gameDisableAutoRefreshCountries: string[];
  magniteSkipCountries?: string[];
  mobileInlineAdTest: {
    fourthAdDisabledSectionIds: (number | string)[];
    fourthAdEnabledSectionIds: (number | string)[];
    isEnabled: boolean;
    isSurveyEnabled: boolean;
  };
};

export function parseConfig(
  raw?: null | RawAdvertisementConfig,
  currentPageType?: PageType,
): AdvertisementConfig | undefined {
  if (!raw) return;

  const isGamePages =
    currentPageType === PageType.Games || currentPageType === PageType.GamesCrosswords;

  const gamesAutoRefreshConfig = {
    desktopMaximumRefreshLimit: raw.gameAutoRefreshDesktopLimit,
    disabled: !raw.gameAdRefreshEnable,
    disabledCountries: raw.gameDisableAutoRefreshCountries,
    disabledResumeRefresh: true,
    mobileMaximumRefreshLimit: raw.gameAutoRefreshMobileLimit,
    refreshIntervalMs: raw.gameAutoRefreshInterval * 1000,
  };

  const parsed: DeepPartial<AdvertisementConfig> = {
    autoRefresh: {
      desktopMaximumRefreshLimit: raw.autoRefreshDesktopLimit,
      disabledCountries: raw.disableAutoRefreshCountries,
      minimalScrollAwayDurationMs: raw.autoRefreshMinimalScrollAwayDurationMs,
      mobileMaximumRefreshLimit: raw.autoRefreshMobileLimit,
      refreshIntervalMs: raw?.autoRefreshInterval && raw.autoRefreshInterval * 1000,
      ...(isGamePages ? gamesAutoRefreshConfig : {}),
    },
    magnitePrebid: {
      skipCountries: raw.magniteSkipCountries ?? [],
    },
    mobileInlineAdTest: {
      fourthAdDisabledSectionIds:
        raw?.mobileInlineAdTest?.fourthAdDisabledSectionIds?.map(String) ?? [],
      fourthAdEnabledSectionIds:
        raw?.mobileInlineAdTest?.fourthAdEnabledSectionIds?.map(String) ?? [],
      isEnabled: raw?.mobileInlineAdTest?.isEnabled ?? false,
      isSurveyEnabled: raw?.mobileInlineAdTest?.isSurveyEnabled ?? false,
    },
    viewabilityTracking: {
      enable: raw.adViewabilityTrackingEnable === 1,
      eventIntervalMs: raw.adViewabilityTrackingEventInterval,
      testMode: {
        enable: raw.adViewabilityTrackingTestMode === 1,
        targetUrlPatterns: raw.adViewabilityTrackingTargetUrls,
      },
      workerIntervalMs: raw.adViewabilityTrackingQueueWorkerInterval,
    },
  };

  const merged = {
    autoRefresh: defaults(parsed.autoRefresh, defaultAdvertisementConfig.autoRefresh),
    magnitePrebid: defaults(parsed.magnitePrebid, defaultAdvertisementConfig.magnitePrebid),
    mobileInlineAdTest: defaults(
      parsed.mobileInlineAdTest,
      defaultAdvertisementConfig.mobileInlineAdTest,
    ),
    viewabilityTracking: defaults(
      parsed.viewabilityTracking,
      defaultAdvertisementConfig.viewabilityTracking,
    ),
  };

  return merged;
}

const defaultAdSlotVariants = {
  large: {
    height: 250,
    padding: 20,
  },
  small: {
    height: 100,
    padding: 10,
  },
} as const;

const styleAdSlotOverrides = {
  small: {
    padding: 20,
  },
} as const;

const styleAdSlotVariants = merge<typeof defaultAdSlotVariants, typeof styleAdSlotOverrides>(
  defaultAdSlotVariants,
  styleAdSlotOverrides,
);

export const useResponsiveAdSlot = (configuration?: AppBarConfiguration) => {
  const atomValue = useAtomValue(topBannerAdSlotAtom);
  const adSlotValue = configuration?.topBannerAdSlot ?? atomValue;

  const adSlotVariants = {
    ...(configuration?.variant === "scmp/magazines-style"
      ? styleAdSlotVariants
      : defaultAdSlotVariants),
    ...configuration?.adSlotVariants,
  };

  const bindAdSlotContainer = useMemo(() => {
    const largeStyles = {
      $height: adSlotVariants.large.height,
      $isVisible: configuration?.hasDesktopAd,
      $padding: adSlotVariants.large.padding,
    };
    const smallStyles = {
      $height: adSlotVariants.small.height,
      $isVisible: configuration?.hasMobileAd,
      $padding: adSlotVariants.small.padding,
    };
    // AMS has no inventory at tablet breakpoint, so hide the container entirely
    const tabletStyles = {
      $height: 0,
      $isVisible: false,
      $padding: 0,
    };

    switch (configuration?.variant) {
      case "scmp/home":
        return {
          $responsiveVariants: {
            desktopUp: largeStyles,
            mobileUp: smallStyles,
            tabletUp: tabletStyles,
          },
        };
      default:
        return {
          $responsiveVariants: {
            desktopUp: largeStyles,
            mobileUp: smallStyles,
            tabletUp: tabletStyles,
          },
        };
    }
  }, [
    adSlotVariants.large.height,
    adSlotVariants.large.padding,
    adSlotVariants.small.height,
    adSlotVariants.small.padding,
    configuration?.hasDesktopAd,
    configuration?.hasMobileAd,
    configuration?.variant,
  ]);

  const isDesktopUp = useResponsive(
    theme.breakpoints.up(configuration?.desktopBreakpoint ?? "desktop"),
  );
  const isTabletOnly = useResponsive(theme.breakpoints.only("tablet"));

  const desktopAdSlot = useMemo(
    () =>
      adSlotValue?.desktop ? (
        <DynamicAdSlot
          breakpoint={theme.breakpoints.up("desktop")}
          sizes={[
            [970, 250],
            [728, 90],
            [970, 90],
          ]}
          {...adSlotValue.desktop}
          manualRefreshCount={adSlotValue.manualRefreshCount}
          targeting={{
            ...adSlotValue.desktop.targeting,
          }}
        />
      ) : null,
    [adSlotValue.desktop, adSlotValue.manualRefreshCount],
  );

  const mobileAdSlot = useMemo(
    () =>
      adSlotValue?.mobile ? (
        <DynamicAdSlot
          breakpoint={theme.breakpoints.down("tablet")}
          sizes={[
            [300, 100],
            [320, 100],
            [300, 50],
            [320, 50],
          ]}
          {...adSlotValue.mobile}
          manualRefreshCount={adSlotValue.manualRefreshCount}
          targeting={{
            ...adSlotValue.mobile.targeting,
          }}
        />
      ) : null,
    [adSlotValue],
  );

  const { adSlotHeight, stickyStyle } = useMemo(() => {
    if (isDesktopUp) {
      return {
        adSlotHeight: desktopAdSlot
          ? adSlotVariants.large.height + adSlotVariants.large.padding * 2
          : 0,
        stickyStyle: "withScrollAwayElement" as const,
      };
    }
    if (isTabletOnly) {
      return {
        adSlotHeight: 0,
        stickyStyle: "withHideElementAfterScrollThreshold" as const,
      };
    }
    return {
      adSlotHeight: mobileAdSlot
        ? adSlotVariants.small.height + adSlotVariants.small.padding * 2
        : 0,
      stickyStyle: "withHideElementAfterScrollThreshold" as const,
    };
  }, [
    desktopAdSlot,
    mobileAdSlot,
    adSlotVariants.large.height,
    adSlotVariants.large.padding,
    adSlotVariants.small.height,
    adSlotVariants.small.padding,
    isDesktopUp,
    isTabletOnly,
  ]);

  return {
    adSlotHeight,
    bindAdSlotContainer,
    desktopAdSlot,
    mobileAdSlot,
    stickyStyle,
  };
};
